import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import "./ThemeBackground.css";

const ThemeVideoContext = createContext(false);
const ThemeKindContext = createContext(false);
const ThemeMediaContext = createContext(null);

export function useIsVideoTheme() {
  return useContext(ThemeKindContext);
}

export function useThemeMedia() {
  return useContext(ThemeMediaContext);
}

const VIDEO_EXT = /\.(mp4|webm)$/i;

export function isThemeVideo(src) {
  if (typeof src !== "string") return false;
  const withoutHash = src.trim().split("#")[0];
  if (!withoutHash) return false;
  try {
    const pathname = new URL(withoutHash, "https://theme.local").pathname;
    return VIDEO_EXT.test(decodeURIComponent(pathname));
  } catch {
    return VIDEO_EXT.test(withoutHash.split("?")[0]);
  }
}

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduce(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  return reduce;
}

function posterStyle(poster, backgroundSize, backgroundPosition, backgroundAttachment) {
  return {
    backgroundImage: `url(${poster})`,
    backgroundRepeat: "no-repeat",
    backgroundSize,
    backgroundPosition: backgroundPosition || "center center",
    backgroundAttachment,
  };
}

export function ThemeBackground({
  src,
  poster,
  className,
  style,
  children,
  allowNestedVideo = false,
  backgroundRepeat = "no-repeat",
  backgroundSize = "cover",
  backgroundPosition,
  backgroundAttachment,
}) {
  const ancestorPlaying = useContext(ThemeVideoContext);
  const reduceMotion = usePrefersReducedMotion();
  const videoRef = useRef(null);
  const [playbackFailed, setPlaybackFailed] = useState(false);
  const isVideo = isThemeVideo(src);
  const suppressedByAncestor = ancestorPlaying && !allowNestedVideo;
  const playVideo =
    isVideo && !reduceMotion && !suppressedByAncestor && !playbackFailed;
  const paintPoster =
    isVideo && !playVideo && !suppressedByAncestor && Boolean(poster);

  useEffect(() => {
    setPlaybackFailed(false);
  }, [src, reduceMotion]);

  useEffect(() => {
    const node = videoRef.current;
    if (!playVideo || !node) return undefined;
    node.muted = true;
    let cancelled = false;
    const fail = () => {
      if (!cancelled) setPlaybackFailed(true);
    };
    node.addEventListener("error", fail);
    const attempt = node.play();
    if (attempt && typeof attempt.catch === "function") {
      attempt.catch(() => {
        if (!cancelled) setPlaybackFailed(true);
      });
    }
    return () => {
      cancelled = true;
      node.removeEventListener("error", fail);
    };
  }, [playVideo, src]);

  const backgroundStyle = !isVideo && src
    ? {
        backgroundImage: `url(${src})`,
        backgroundRepeat,
        backgroundSize,
        backgroundPosition,
        backgroundAttachment,
      }
    : paintPoster
      ? posterStyle(poster, backgroundSize, backgroundPosition, backgroundAttachment)
      : playVideo
        ? { backgroundImage: "none" }
        : null;

  return (
    <ThemeVideoContext.Provider
      value={allowNestedVideo ? playVideo : ancestorPlaying || playVideo}
    >
      <ThemeKindContext.Provider value={isVideo}>
        <ThemeMediaContext.Provider
          value={isVideo ? { src, poster: poster || null, playVideo } : null}
        >
          <div
            data-testid="theme-bg-host"
            className={`${className || ""} ${playVideo ? "theme-bg-host" : ""}`.trim()}
            style={{ ...style, ...backgroundStyle }}
          >
            {playVideo && (
              <video
                ref={(node) => {
                  videoRef.current = node;
                  if (node) {
                    node.defaultMuted = true;
                    node.muted = true;
                  }
                }}
                className={
                  backgroundAttachment === "fixed"
                    ? "theme-bg-video theme-bg-video--fixed"
                    : "theme-bg-video"
                }
                data-testid="theme-bg-video"
                src={src}
                poster={poster || undefined}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-hidden="true"
                onError={() => setPlaybackFailed(true)}
              />
            )}
            {children}
          </div>
        </ThemeMediaContext.Provider>
      </ThemeKindContext.Provider>
    </ThemeVideoContext.Provider>
  );
}
